import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [countdown, setCountdown] = useState(3);

  // WICHTIG: Automatische API-URL Erkennung ohne doppeltes /api
  const getApiUrl = () => {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:3001/api';
    }
    return 'https://app.alpenscreen.ch/api';
  };

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setErrorMessage('Kein Verification-Token gefunden.');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const API_URL = getApiUrl();
      console.log('API Request to:', `${API_URL}/auth/verify-email?token=${token}`); // Debug log
      
      const response = await fetch(`${API_URL}/auth/verify-email?token=${token}`);
      const data = await response.json();

      if (response.ok) {
        // Token und User SOFORT speichern
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          localStorage.setItem('userId', data.user.id);
          localStorage.setItem('userRole', data.user.role);
        }
        
        // Kleine Verzögerung damit localStorage garantiert gespeichert ist
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setStatus('success');
        
        // Countdown und Redirect
        let seconds = 3;
        const interval = setInterval(() => {
          seconds--;
          setCountdown(seconds);
          
          if (seconds === 0) {
            clearInterval(interval);
            navigate('/dashboard');
          }
        }, 1000);
      } else {
        setStatus('error');
        setErrorMessage(data.error || 'Verification fehlgeschlagen.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      setErrorMessage('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        padding: '60px 40px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center'
      }}>
        {/* Loading State */}
        {status === 'loading' && (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Loader2 
                size={60} 
                color="#2B7FD7" 
                style={{
                  animation: 'spin 1s linear infinite'
                }}
              />
            </div>
            <h1 style={{
              fontSize: '28px',
              color: '#334155',
              marginBottom: '16px',
              fontWeight: '700'
            }}>
              Email wird verifiziert...
            </h1>
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.6'
            }}>
              Bitte warten Sie einen Moment.
            </p>
          </>
        )}

        {/* Success State */}
        {status === 'success' && (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 30px',
              boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)'
            }}>
              <CheckCircle size={48} color="white" strokeWidth={2.5} />
            </div>
            <h1 style={{
              fontSize: '28px',
              color: '#334155',
              marginBottom: '16px',
              fontWeight: '700'
            }}>
              Email erfolgreich bestätigt!
            </h1>
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              marginBottom: '30px',
              lineHeight: '1.6'
            }}>
              Ihre Email-Adresse wurde erfolgreich verifiziert. Sie werden in{' '}
              <strong style={{ color: '#2B7FD7' }}>{countdown}</strong> Sekunden zum Dashboard weitergeleitet.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                background: 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
                color: 'white',
                padding: '14px 32px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(43, 127, 215, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(43, 127, 215, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(43, 127, 215, 0.3)';
              }}
            >
              Jetzt zum Dashboard
            </button>
          </>
        )}

        {/* Error State */}
        {status === 'error' && (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 30px',
              boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)'
            }}>
              <XCircle size={48} color="white" strokeWidth={2.5} />
            </div>
            <h1 style={{
              fontSize: '28px',
              color: '#334155',
              marginBottom: '16px',
              fontWeight: '700'
            }}>
              Verification fehlgeschlagen
            </h1>
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              marginBottom: '30px',
              lineHeight: '1.6'
            }}>
              {errorMessage}
            </p>
            <button
              onClick={() => navigate('/register')}
              style={{
                background: 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
                color: 'white',
                padding: '14px 32px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(43, 127, 215, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(43, 127, 215, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(43, 127, 215, 0.3)';
              }}
            >
              Zurück zur Registrierung
            </button>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VerifyEmail;